import React from 'react';
import { ConfigProvider, Tooltip } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import { AgenticQuickActionsProps, QuickAction, DEFAULT_QUICK_ACTIONS } from '../AskAsperAgentic.types';

// Trend Up Icon
const TrendUpIcon: React.FC<{ size?: number; color?: string }> = ({ size = 24, color = '#000' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M22 7L13.5 15.5L8.5 10.5L2 17"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M16 7H22V13" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Bar Chart Icon
const BarChartIcon: React.FC<{ size?: number; color?: string }> = ({ size = 24, color = '#000' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 20V10" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 20V4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18 20V14" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Atom/Simulation Icon
const AtomIcon: React.FC<{ size?: number; color?: string }> = ({ size = 24, color = '#000' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="12" cy="12" rx="10" ry="4" stroke={color} strokeWidth="2" transform="rotate(45 12 12)" />
        <ellipse cx="12" cy="12" rx="10" ry="4" stroke={color} strokeWidth="2" transform="rotate(-45 12 12)" />
        <circle cx="12" cy="12" r="2" fill={color} />
    </svg>
);

// Pie Chart Icon for Project
const PieChartIcon: React.FC<{ size?: number; color?: string }> = ({ size = 24, color = '#000' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M21.21 15.89C20.5738 17.3945 19.5788 18.7202 18.3119 19.7513C17.045 20.7824 15.5447 21.4874 13.9424 21.8048C12.3401 22.1221 10.6844 22.0421 9.12006 21.5718C7.55574 21.1015 6.13029 20.2551 4.96903 19.1066C3.80777 17.9582 2.94549 16.5425 2.45763 14.9839C1.96977 13.4252 1.87102 11.7711 2.17004 10.1659C2.46907 8.56079 3.1573 7.05343 4.17438 5.77632C5.19146 4.49922 6.5064 3.49086 8 2.84"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M22 12C22 10.6868 21.7413 9.38642 21.2388 8.17317C20.7362 6.95991 19.9997 5.85752 19.0711 4.92893C18.1425 4.00035 17.0401 3.26375 15.8268 2.76120C14.6136 2.25866 13.3132 2 12 2V12H22Z"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// File/Report Icon
const FileIcon: React.FC<{ size?: number; color?: string }> = ({ size = 24, color = '#000' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M14 2V8H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 13H8" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 17H8" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M10 9H9H8" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Get icon based on type
const getIconForType = (iconType?: string, size = 24, color = '#000') => {
    switch (iconType) {
        case 'trend':
            return <TrendUpIcon size={size} color={'#42A531'} />;
        case 'portfolio':
            return <BarChartIcon size={size} color={'#E06936'} />;
        case 'simulation':
            return <AtomIcon size={size} color={'#DD449F'} />;
        case 'project':
            return <PieChartIcon size={size} color={'#EDB44E'} />;
        case 'report':
            return <FileIcon size={size} color={'#4557D5'} />;
        default:
            return null;
    }
};

// Single Quick Action Pill
const QuickActionPill: React.FC<{
    action: QuickAction;
    onClick?: () => void;
    'data-test-id'?: string;
}> = ({ action, onClick, 'data-test-id': dataTestId }) => {
    const icon = action.icon || getIconForType(action.iconType, 16, '#3B2C77');
    const tooltipTitle = action.fullQuestion || action.label;

    const button = (
        <button
            type="button"
            className="bg-white border border-[#e8eafa] rounded-[27px] flex gap-2 items-center h-9 px-3 py-5 cursor-pointer hover:bg-[#fafafa] hover:border-[#d0d5f5] transition-colors relative overflow-hidden"
            onClick={onClick}
            data-test-id={dataTestId}
        >
            {/* Blur backdrop */}
            <div className="absolute inset-[-1px] bg-white/35 backdrop-blur-[16px]" />
            {icon && <span className="relative z-10">{icon}</span>}
            <span className="font-['Open_Sans'] font-normal text-sm leading-6 text-black whitespace-nowrap relative z-10">
                {action.label}
            </span>
        </button>
    );

    // Wrap with tooltip if fullQuestion exists
    if (action.fullQuestion) {
        return (
            <Tooltip title={tooltipTitle} placement="top">
                {button}
            </Tooltip>
        );
    }

    return button;
};

/**
 * AgenticQuickActions - Quick action prompt pills
 * Part of the AskAsper Agentic Platform components
 */
const AgenticQuickActions: React.FC<AgenticQuickActionsProps> = ({
    actions = DEFAULT_QUICK_ACTIONS,
    onActionClick,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();

    // Split actions into two rows
    const topRowActions = actions.slice(0, 3);
    const bottomRowActions = actions.slice(3);

    const handleActionClick = (action: QuickAction) => {
        if (onActionClick) {
            onActionClick(action);
        }
    };

    return (
        <ConfigProvider theme={theme}>
            <div
                className="flex flex-col gap-[11px] items-center w-full max-w-[600px]"
                data-test={dataTest}
                data-test-id={dataTestId}
                data-agentic-quick-actions
            >
                {/* Top Row */}
                <div className="flex gap-3 items-center justify-center w-full flex-wrap">
                    {topRowActions.map((action) => (
                        <QuickActionPill
                            key={action.id}
                            action={action}
                            onClick={() => handleActionClick(action)}
                            data-test-id={`${dataTestId}-${action.id}`}
                        />
                    ))}
                </div>

                {/* Bottom Row */}
                {bottomRowActions.length > 0 && (
                    <div className="flex gap-3 items-center justify-center flex-wrap">
                        {bottomRowActions.map((action) => (
                            <QuickActionPill
                                key={action.id}
                                action={action}
                                onClick={() => handleActionClick(action)}
                                data-test-id={`${dataTestId}-${action.id}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </ConfigProvider>
    );
};

export default AgenticQuickActions;
